import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  DesktopWindowsOutlined as DesktopWindowsOutlinedIcon,
  MoreHoriz as MoreHorizIcon,
  Search as SearchIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import StatCard from '../../Components/GatewayContagem/StatCard';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import EditDeviceModal from '../../Components/Modal/EditDeviceModal';
import ClientBadge from '../../Components/GatewayContagem/ClientBadge';
import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';
import usePermissions from '../../Context/Hooks/usePermissions';
import { PERMISSIONS } from '../../Constants/permissions';
import { deviceAPI } from '../../API/deviceAPI';
import { storeAPI } from '../../API/storeAPI';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import useCustomAuth from '../../Context/Hooks/useCustomAuth';
import { buildQueryString, TABLE_HEAD_ROW_SX } from '../../Utils/format';

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  ONLINE: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', dot: '#22c55e' },
  WARNING: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', dot: '#f59e0b' },
  OFFLINE: { bg: '#fef2f2', color: '#e2453b', border: '#fadcd9', dot: '#ef4444' },
  MAINTENANCE: { bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd', dot: '#38bdf8' },
};

const BADGE_COLORS = ['#ea580c', '#7c3aed', '#0d9488', '#059669', '#d97706', '#1B75BB', '#db2777', '#2563eb'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const computeLastContact = (device) => {
  if (device.status === 'ONLINE') return null;
  const d = new Date(device.lastHeartbeat);
  if (isNaN(d)) return null;
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 60) return `${mins}min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// eslint-disable-next-line react/prop-types
const StatusBadge = ({ status }) => {
  const { t } = useTranslation('devices');
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.OFFLINE;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        px: '8px',
        py: '3px',
        borderRadius: '999px',
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      {t(`status.${status}`, status)}
    </Box>
  );
};

// eslint-disable-next-line react/prop-types
const DeviceRowMenu = ({ device, onEdit }) => {
  const { t } = useTranslation('devices');
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [anchor, setAnchor] = useState(null);

  const canManage = can(PERMISSIONS.DEVICES_MANAGE);
  const canViewDetail = can(PERMISSIONS.USER_DETAIL_VIEW);

  const handleAction = (key) => {
    setAnchor(null);
    if (key === 'viewDetails') navigate(`/Gateway/Devices/${device.id}`);
    if (key === 'edit') onEdit(device);
  };

  const items = [
    ...(canViewDetail ? [{ key: 'viewDetails', label: t('menu.viewDetails') }] : []),
    ...(canManage ? [{ key: 'edit', label: t('menu.edit') }] : []),
  ];

  if (items.length === 0) return null;
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 180 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {items.map(({ key, label, color }) => (
          <MenuItem key={key} onClick={() => handleAction(key)} sx={{ fontSize: 13, ...(color && { color }) }}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const DevicesPage = () => {
  const { t } = useTranslation(['devices', 'common']);
  const { clients } = useGatewayContagem();
  const { user: authUser } = useCustomAuth();
  const { can } = usePermissions();
  const canViewDetail = can(PERMISSIONS.USER_DETAIL_VIEW);
  const canManage = can(PERMISSIONS.DEVICES_MANAGE);
  const currentUserRole = authUser?.role ?? 'USER';
  const currentUserClientId = authUser?.clientId ?? null;
  const userStoreIds = useMemo(() => {
    if (currentUserRole === 'GATEWAY_ADMIN' || !authUser?.storeIds?.length) return [];
    return authUser.storeIds;
  }, [currentUserRole, authUser?.storeIds]);

  const [devices, setDevices] = useState([]);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [storeMap, setStoreMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState(() => (
    currentUserRole !== 'GATEWAY_ADMIN' && currentUserClientId ? [currentUserClientId] : []
  ));
  const [statusFilter, setStatusFilter] = useState([]);
  const [modelFilter, setModelFilter] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);

  useEffect(() => {
    if (currentUserRole !== 'GATEWAY_ADMIN' && currentUserClientId) {
      setClientFilter([currentUserClientId]);
    }
  }, [currentUserRole, currentUserClientId]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Stats state populated from backend
  const [stats, setStats] = useState({ total: 0, online: 0, warning: 0, offline: 0, onlinePercent: 0 });

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);

  // Load devices and stats with server-side pagination and counts
  const loadDevices = useCallback(async () => {
    setLoading(true);

    try {
      const result = await deviceAPI.list(buildQueryString({
        page: page + 1,
        itemsPerPage: rowsPerPage,
        clientIds: clientFilter.filter(Boolean),
        storeIds: userStoreIds.length > 0 ? userStoreIds : undefined,
        statuses: statusFilter.filter(Boolean),
        models: modelFilter.filter(Boolean),
        search: debouncedSearchQuery.trim(),
      }));

      if (!result || result.error) {
        setDevices([]);
        setDevicesTotal(0);
        toast.error(t('feedback.errorLoadingDevices'));
        return;
      }

      const paginatedResult = result.value;
      let items = Array.isArray(paginatedResult?.items) ? paginatedResult.items : [];

      if (userStoreIds.length > 0) {
        items = items.filter(d => d.storeId && userStoreIds.includes(d.storeId));
      }

      setDevices(items);
      setDevicesTotal(userStoreIds.length > 0 ? items.length : (paginatedResult?.totalCount ?? 0));

      // Load true stats across all pages for the current client & store selection
      const statsRes = await deviceAPI.getStatsByClient(buildQueryString({
        clientIds: clientFilter.filter(Boolean),
        storeIds: userStoreIds.length > 0 ? userStoreIds : undefined,
      }));
      if (statsRes && !statsRes.error && statsRes.value) {
        const { total, online, warning, offline } = statsRes.value;
        setStats({
          total,
          online,
          warning,
          offline,
          onlinePercent: total ? Math.round((online / total) * 100) : 0,
        });
      } else {
        setStats({ total: 0, online: 0, warning: 0, offline: 0, onlinePercent: 0 });
      }
    } catch {
      toast.error(t('feedback.errorLoadingDevices'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, clientFilter, statusFilter, modelFilter, debouncedSearchQuery, userStoreIds, t]);

  const clientMap = useMemo(() => {
    const map = {};

    (clients ?? []).forEach((client, index) => {
      map[client.id] = {
        id: client.id,
        name: client.name,
        color: BADGE_COLORS[index % BADGE_COLORS.length],
      };
    });

    return map;
  }, [clients]);

  const loadStoresLookup = useCallback(async () => {
    try {
      const storeRes = await storeAPI.list({
        page: 1,
        itemsPerPage: 500,
      });

      const stores = storeRes?.value?.items ?? [];
      const map = {};

      stores.forEach((store) => {
        if (userStoreIds.length === 0 || userStoreIds.includes(store.id)) {
          map[store.id] = store.name;
        }
      });

      setStoreMap(map);
    } catch {
      toast.error(t('feedback.errorLoadingStores', 'Erro ao carregar lojas.'));
    }
  }, [userStoreIds, t]);

  useEffect(() => {
    loadStoresLookup();
  }, [loadStoresLookup]);

  useEffect(() => {
    deviceAPI.getDistinctModels().then((res) => {
      if (res && !res.error && Array.isArray(res.value)) {
        setAvailableModels(res.value);
      }
    });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery, clientFilter.join(','), statusFilter.join(','), modelFilter.join(',')]);

  // Load devices when page, rowsPerPage, or filters change
  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const isSearchLoading = searchQuery !== debouncedSearchQuery;
  const isLoading = loading || isSearchLoading;

  const hasActiveFilters = useMemo(() => {
    const hasSearch = Boolean(searchQuery && searchQuery.trim().length > 0);
    const hasStatus = statusFilter.filter(Boolean).length > 0;
    const hasModel = modelFilter.filter(Boolean).length > 0;
    const hasClient = currentUserRole === 'GATEWAY_ADMIN' && clientFilter.filter(Boolean).length > 0;
    return hasSearch || hasStatus || hasModel || hasClient;
  }, [searchQuery, statusFilter, modelFilter, clientFilter, currentUserRole]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter([]);
    setModelFilter([]);
    if (currentUserRole === 'GATEWAY_ADMIN') {
      setClientFilter([]);
    } else if (currentUserClientId) {
      setClientFilter([currentUserClientId]);
    }
  }, [currentUserRole, currentUserClientId]);

  // Devices are already filtered and paginated server-side by loadDevices()
  const displayedDevices = devices;

  const handleEditClick = (device) => {
    setEditingDevice(device);
    setEditDialogOpen(true);
  };

  const statusOptions = [
    { value: '', label: t('filters.allStatuses') },
    { value: 'ONLINE', label: t('status.ONLINE') },
    { value: 'WARNING', label: t('status.WARNING') },
    { value: 'OFFLINE', label: t('status.OFFLINE') },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {/* ── KPI cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.total')}
            value={stats.total}
            icon={<DesktopWindowsOutlinedIcon fontSize="inherit" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.online')}
            value={
              <>
                {stats.online}
                <Typography component="span" sx={{ fontSize: 16, fontWeight: 500, color: '#c0c4cc', ml: '6px' }}>
                  {stats.onlinePercent}%
                </Typography>
              </>
            }
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            accentColor="#2E9E5B"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.warning')}
            value={stats.warning}
            icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
            accentColor="#E0A800"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.offline')}
            value={stats.offline}
            icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
            accentColor="#D64545"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ── Table card ── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        }}
      >
        {/* ── Filter bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              placeholder={t('filters.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                '& .MuiInputBase-root': { fontSize: 13 },
                '& .MuiOutlinedInput-root fieldset': { borderColor: 'divider' },
              }}
            />
            {currentUserRole === 'GATEWAY_ADMIN' && (
              <Select
                multiple
                size="small"
                value={clientFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setClientFilter(typeof val === 'string' ? val.split(',') : val);
                }}
                displayEmpty
                renderValue={(selected) => (
                  selected.length === 0
                    ? <span style={{ opacity: 0.55 }}>{t('filters.allClients')}</span>
                    : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const client = clients.find((c) => c.id === id);
                          return <Chip key={id} label={client?.name || id} size="small" />;
                        })}
                      </Box>
                )}
                sx={{
                  fontSize: 13,
                  minWidth: 170,
                  maxWidth: 280,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                }}
              >
                {(clients ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>
                    <Checkbox checked={clientFilter.includes(c.id)} size="small" />
                    <ListItemText primary={c.name} />
                  </MenuItem>
                ))}
              </Select>
            )}
            <Select
              multiple
              size="small"
              value={statusFilter}
              onChange={(e) => {
                const val = e.target.value;
                setStatusFilter(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={(selected) => (
                selected.length === 0
                  ? <span style={{ opacity: 0.55 }}>{t('filters.allStatuses')}</span>
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => {
                        const opt = statusOptions.find((o) => o.value === val);
                        return <Chip key={val} label={opt?.label || val} size="small" />;
                      })}
                    </Box>
              )}
              sx={{
                fontSize: 13,
                minWidth: 170,
                maxWidth: 280,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            >
              {statusOptions.filter(o => o.value !== '').map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                  <Checkbox size="small" checked={statusFilter.includes(opt.value)} />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
            <Select
              multiple
              size="small"
              value={modelFilter}
              onChange={(e) => {
                const val = e.target.value;
                setModelFilter(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={(selected) => (
                selected.length === 0
                  ? <span style={{ opacity: 0.55 }}>{t('filters.allModels', 'Todos os modelos')}</span>
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
              )}
              sx={{
                fontSize: 13,
                minWidth: 170,
                maxWidth: 280,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            >
              {availableModels.map((model) => (
                <MenuItem key={model} value={model} sx={{ fontSize: 13 }}>
                  <Checkbox size="small" checked={modelFilter.includes(model)} />
                  <ListItemText primary={model} />
                </MenuItem>
              ))}
            </Select>
            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearFilters}
                sx={{ textTransform: 'none', fontSize: 13 }}
              >
                {t('filters.clearFilters', 'Limpar filtros')}
              </Button>
            )}
          </Stack>

          <Box sx={{ minWidth: 90, display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingSkeleton loading={isLoading} preset="text" width={80} height={18}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {t('filters.count', { count: displayedDevices.length })}
              </Typography>
            </LoadingSkeleton>
          </Box>
        </Box>

        {/* ── Table ── */}
        <LoadingSkeleton loading={isLoading} preset="table" height={360} sx={{ borderRadius: 0 }}>
          <TableContainer sx={{ minHeight: 250 }}>
            <Table size="small" sx={{ minWidth: 900, '& .MuiTableCell-root': { fontSize: 13, borderColor: 'divider' } }}>
              <TableHead>
                <TableRow sx={TABLE_HEAD_ROW_SX}>
                  <TableCell sx={{ pl: 2.5 }}>{t('table.status')}</TableCell>
                  <TableCell>{t('table.name')}</TableCell>
                  <TableCell>{t('table.model')}</TableCell>
                  <TableCell>{t('table.storeClient')}</TableCell>
                  <TableCell>{t('table.firmware')}</TableCell>
                  <TableCell>{t('table.lastContact')}</TableCell>
                  <TableCell align="right" sx={{ pr: 1.5, width: 52 }} />
                </TableRow>
              </TableHead>

              <TableBody>
              {displayedDevices.map((device) => {
                const clientInfo = clientMap[device.clientId];
                const storeName = storeMap[device.storeId] ?? '—';
                const lastContact = computeLastContact(device);

                return (
                  <TableRow
                    key={device.id}
                    sx={{
                      '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
                      '&:hover': { bgcolor: '#f9fafb' },
                    }}
                  >
                    <TableCell sx={{ pl: 2.5 }}>
                      <StatusBadge status={device.status} />
                    </TableCell>
                    <TableCell>
                      {canViewDetail ? (
                        <Link
                          component={RouterLink}
                          to={`/Gateway/Devices/${device.id}`}
                          underline="none"
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'text.primary',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            '&:hover': {
                              color: 'primary.main',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {device.name || '—'}
                        </Link>
                      ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
                          {device.name || '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                        {device.model || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {device.storeId && storeName && storeName !== '—' ? (
                        <Link
                          component={RouterLink}
                          to={`/Gateway/Stores/${device.storeId}`}
                          underline="hover"
                          sx={{ fontSize: 13, fontWeight: 500, color: 'primary.main', display: 'block', lineHeight: 1.3 }}
                        >
                          {storeName}
                        </Link>
                      ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                          {storeName}
                        </Typography>
                      )}
                      {clientInfo && (
                        (currentUserRole === 'GATEWAY_ADMIN' || currentUserRole === 'CLIENT_ADMIN') ? (
                          <Link
                            component={RouterLink}
                            to={`/Gateway/Clients/${device.clientId}`}
                            underline="none"
                            sx={{ display: 'inline-block', mt: 0.5 }}
                          >
                            <ClientBadge clientName={clientInfo.name} />
                          </Link>
                        ) : (
                          <Box sx={{ display: 'inline-block', mt: 0.5 }}>
                            <ClientBadge clientName={clientInfo.name} />
                          </Box>
                        )
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {device.firmwareVersion || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {lastContact ?? t('table.now')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1.5, width: 52 }}>
                      <DeviceRowMenu device={device} onEdit={handleEditClick} />
                    </TableCell>
                  </TableRow>
                );
              })}

              {!isLoading && devices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 5, textAlign: 'center', border: 'none' }}>
                    <Typography variant="body2" color="text.disabled">
                      {t('table.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </LoadingSkeleton>
        <TablePagination
          component="div"
          count={devicesTotal}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          showFirstButton
          showLastButton
          labelRowsPerPage={t('common:pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
          sx={{ borderTop: '1px solid', borderColor: 'divider', fontSize: 13 }}
        />
      </Box>

      {canManage && (
        <EditDeviceModal
          open={editDialogOpen}
          device={editingDevice}
          storeName={editingDevice ? storeMap[editingDevice.storeId] : ''}
          clientName={editingDevice ? clientMap[editingDevice.clientId]?.name : ''}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingDevice(null);
          }}
          onSuccess={() => {
            loadDevices();
          }}
        />
      )}
    </Box>
  );
};

export default DevicesPage;
